import { useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
    AgeingBar, Button, Card, EmptyState, LoadingList, PageHeader, ProportionBar, Segmented, Table, Td, Th, RowHead, ageSwatch, cx,
    useIndicator,
} from '@timely/ui';
import {
    CHEQUE_STATES, DEFAULT_FILTERS, UNPLANNED_QUERY, bookRow, dayIsClear, followUpCoverage, holds,
    worklistGroup, worklistQuery,
    type BookFilters, type BookSort, type ChequeState, type WorklistFigure, type WorklistGroup, type WorklistTotals,
} from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { paths } from '../workspace/navigation';
import { FormNotice } from './AuthLayout';
import { useFormatters } from '../book/useFormatters';
import { useMineCounts, useToday, type MemberWorkload, type TodayAgeingBand } from '../book/useToday';
import { useBook, useLastNotes } from '../book/useBook';
import { AccountTable, assigneeName } from '../book/AccountTable';
import { useOnboarding } from '../onboarding/useOnboarding';
import { WorkspaceHome } from './WorkspaceHome';
import { agedRows, ageingRowLabel } from '../receivables/ageingReport';
import { SourceFreshness } from '../receivables/SourceFreshness';
import { usePriorityReasons } from '../book/usePriorityReasons';

/**
 * Today: where work starts (PRODUCT-UX-FOUNDATION §7).
 *
 * The queue is the screen. The groups — late, due today, urgent, no date
 * set, scheduled — are a selector over one list of the accounts themselves, so
 * a collector reads it, presses the first account, records what happened, and
 * moves to the next without going anywhere else. Opening an account carries
 * the queue with it: the account's "Next" walks this same list.
 *
 * Every count and the list under it are the same question. The counts come from
 * `worklist_summary()` (or, for a manager's own share, `book_totals()` with
 * `owner = mine`), and the list from `book_page()` under the group's filter from
 * `worklistQuery()`. What a person sees is what they may see: every one of
 * those is `security invoker`. The team panel needs `customers.view.all`, because
 * a team figure is misleading when it is a total of one person's share.
 */

/** The queue's groups, in the order somebody works them. Accounts marked as bad debt are recovery, not routine work, and sit outside it. */
const QUEUE: readonly WorklistGroup[] = ['overdue', 'today', 'urgent', 'no_follow_up', 'future'];

const SHORT_LABEL: Record<WorklistGroup, string> = {
    // A follow-up whose date has gone by is late; overdue is money past its due date (Customer 360, F1).
    overdue: 'Late', today: 'Due today', urgent: 'Urgent', no_follow_up: 'No date set', future: 'Scheduled', bad_debt: 'Bad debt',
};

/** How each group is best worked: the longest-waiting first, or the most money first. */
const QUEUE_SORT: Record<WorklistGroup, BookSort> = {
    overdue: 'follow-up', today: 'amount-desc', urgent: 'amount-desc', no_follow_up: 'amount-desc', future: 'follow-up', bad_debt: 'amount-desc',
};

const ORDER_NOTE: Record<WorklistGroup, string> = {
    overdue: 'Longest waiting first.', today: 'Largest amount first.', urgent: 'Largest amount first.',
    no_follow_up: 'Largest amount first. Give each a date.', future: 'Soonest first.', bad_debt: 'Largest amount first.',
};

export function Today() {
    const context = useActiveWorkspace();
    const { workspace } = context;
    const { money, count } = useFormatters();
    const to = paths(workspace.slug);

    const wholeBook = holds(context, 'customers.view.all');
    const canSeeTeam = wholeBook && holds(context, 'team.view');
    const canSeeCheques = holds(context, 'cheques.view');
    const canSeeAgeing = holds(context, 'reports.view.own');

    const { totals, team, cheques, chequesUnavailable, ageing, loading, error, reload } =
        useToday(context, { withTeam: canSeeTeam, withCheques: canSeeCheques, withAgeing: canSeeAgeing });
    const onboarding = useOnboarding(workspace.id);

    const [params, setParams] = useSearchParams();
    const mine = wholeBook && params.get('scope') === 'mine';
    const mineCounts = useMineCounts(context, mine);
    const shownFigures = useRef<Record<WorklistGroup, WorklistFigure> | null>(null);
    const fresh: Record<WorklistGroup, WorklistFigure> | null = mine ? mineCounts : totals?.groups ?? null;
    if (fresh) shownFigures.current = fresh;
    const figures = fresh ?? shownFigures.current;
    const countsPending = fresh === null && figures !== null;

    const asked = params.get('group') as WorklistGroup | null;
    const group: WorklistGroup = asked && QUEUE.includes(asked)
        ? asked
        : QUEUE.find(g => (figures?.[g].count ?? 0) > 0) ?? 'overdue';

    const choose = (changes: { group?: WorklistGroup; scope?: 'mine' | 'all' }) => {
        const next = new URLSearchParams(params);
        if (changes.group) next.set('group', changes.group);
        if (changes.scope) { if (changes.scope === 'mine') next.set('scope', 'mine'); else next.delete('scope'); }
        setParams(next, { replace: true });
    };

    if (loading || onboarding.loading) {
        return (
            <Page width="wide">
                <PageHeader title="Today" subtitle="Who to contact now." />
                <Card className="overflow-hidden"><LoadingList label="Loading your day" rows={6} /></Card>
            </Page>
        );
    }

    if (error) {
        return (
            <Page width="wide">
                <PageHeader title="Today" subtitle="Who to contact now." />
                <FormNotice tone="error">{error}</FormNotice>
                <div className="mt-4"><Button variant="secondary" onClick={() => { void reload(); }}>Try again</Button></div>
            </Page>
        );
    }

    // No accounts at all is not a quiet day. A company that never finished
    // onboarding gets the checklist; one between imports (a Fresh Start leaves
    // `completed_at` alone) is told so.
    if (!totals || totals.total.count === 0) {
        if (!onboarding.completedAt) return <WorkspaceHome />;
        return (
            <Page width="wide">
                <PageHeader title="Today" subtitle="Who to contact now." />
                <Card>
                    <EmptyState
                        title="No accounts right now"
                        hint="No customers are loaded at the moment. The next update from your receivables data — TallyPrime or the sheet — will bring them back."
                        action={holds(context, 'datasource.view')
                            ? <Link to={to.setting('data')} className="text-accent-text hover:underline text-body font-semibold">Go to receivables data</Link>
                            : undefined}
                    />
                </Card>
            </Page>
        );
    }

    const clear = !mine && dayIsClear(totals);

    return (
        <Page width="wide">
            <PageHeader
                title="Today"
                subtitle="Who to contact now. Open an account, record what happened, set the next date, and move on."
                actions={
                    <div className="text-right">
                        <div className="text-title font-semibold text-label num leading-tight">{money.exact(totals.total.amount)}</div>
                        <div className="text-caption text-label-3 mt-0.5">
                            Owed across {count(totals.total.count)} {totals.total.count === 1 ? 'account' : 'accounts'}
                        </div>
                    </div>
                }
            />

            {/* How old these figures are, before anybody acts on them (Phase 0). */}
            <SourceFreshness className="-mt-2 mb-4" />

            <div className="grid gap-6 xl:grid-cols-aside 2xl:grid-cols-aside-wide items-start">
                <Card className="overflow-hidden min-w-0">
                    {/* The groups are tabs over one list: the count says how much work each holds. */}
                    <div className="border-b border-separator px-2">
                        <QueueGroups active={group}>
                            {QUEUE.map(g => {
                                const figure = figures?.[g];
                                const active = g === group;
                                const waiting = (figure?.count ?? 0) > 0;
                                return (
                                    <button
                                        key={g}
                                        type="button"
                                        data-active={active}
                                        aria-pressed={active}
                                        aria-label={figure
                                            ? `${SHORT_LABEL[g]}: ${count(figure.count)} ${figure.count === 1 ? 'account' : 'accounts'}`
                                            : SHORT_LABEL[g]}
                                        onClick={() => choose({ group: g })}
                                        className={cx(
                                            'flex-none inline-flex items-center gap-2 h-12 px-3 text-body font-semibold transition-colors duration-150 whitespace-nowrap',
                                            active ? 'text-label' : 'text-label-3 hover:text-label',
                                        )}
                                    >
                                        {SHORT_LABEL[g]}
                                        {/* The count is a figure beside the word, not a pill that swaps its fill from one group to
                                            the next; late follow-ups waiting are amber, as waiting is everywhere (02 §19.2). */}
                                        <span className={cx('num text-secondary font-medium transition-[color,opacity] duration-150',
                                            g === 'overdue' && waiting ? 'text-warn font-semibold' : 'text-label-3', countsPending && 'opacity-50')}>
                                            {figure ? count(figure.count) : '…'}
                                        </span>
                                    </button>
                                );
                            })}
                        </QueueGroups>
                    </div>

                    {clear ? (
                        <EmptyState
                            title="Nothing is waiting"
                            hint="No follow-up is late, none is due today, nothing is flagged, and every account that owes has a date."
                            action={totals.groups.future.count > 0 ? (
                                <Button variant="secondary" onClick={() => choose({ group: 'future' })}>
                                    See the {count(totals.groups.future.count)} scheduled ahead
                                </Button>
                            ) : undefined}
                        />
                    ) : (
                        <Queue group={group} mine={mine} figure={figures?.[group] ?? null} scope={wholeBook ? (
                            // Whose accounts: a filter over the open group, so it sits on the list's own line.
                            <Segmented
                                label="Whose accounts"
                                value={mine ? 'mine' : 'all'}
                                onChange={value => choose({ scope: value })}
                                options={[{ value: 'all', label: 'Everyone' }, { value: 'mine', label: 'Mine' }] as const}
                            />
                        ) : null} />
                    )}

                    {totals.groups.bad_debt.count > 0 && !mine && (
                        <div className="px-5 py-3 border-t border-separator text-secondary text-label-3">
                            {count(totals.groups.bad_debt.count)} {totals.groups.bad_debt.count === 1 ? 'account marked as bad debt is' : 'accounts marked as bad debt are'} not in this list.{' '}
                            <Link to={`${to.customers}?${worklistQuery('bad_debt')}`} className="text-accent-text font-semibold hover:underline">See them</Link>
                        </div>
                    )}
                </Card>

                {/* Secondary, and quiet about it: what was promised, the cheques to act on, who is behind. One soft
                    surface, its parts divided by a hairline — not a stack of five outlined cards. */}
                <div className="min-w-0 rounded-card bg-card divide-y divide-separator empty:hidden">
                    {!mine && <CoveragePanel totals={totals} />}
                    {!mine && totals.promisedOpen.count > 0 && <Promised totals={totals} />}
                    {canSeeCheques && cheques && <ChequeAttention cheques={cheques} unavailable={chequesUnavailable} />}
                    {!mine && canSeeAgeing && <AgeingPanel bands={ageing} />}
                    {canSeeTeam && team.length > 1 && <TeamWork team={team} />}
                </div>
            </div>
        </Page>
    );
}

/* -------------------------------------------------------------------------- */

/** The accounts in one group, as the shared list, and the way to more of them. */
function Queue({ group, mine, figure, scope }: { group: WorklistGroup; mine: boolean; figure: WorklistFigure | null; scope: React.ReactNode }) {
    const context = useActiveWorkspace();
    const { count, today } = useFormatters();
    const to = paths(context.workspace.slug);
    const definition = worklistGroup(group);
    const filters: BookFilters = useMemo(
        () => ({ ...DEFAULT_FILTERS, ...definition.filters, owner: mine ? 'mine' : 'any' }),
        [definition, mine],
    );
    const sort = QUEUE_SORT[group];
    const { customers, members, loading, loadingMore, error, reload, loadMore, hasMore, loadedCount } =
        useBook(context, { filters, sort });

    const names = useMemo(() => new Map(members.map(m => [m.memberId, m.displayName || m.code || 'Somebody'])), [members]);
    const rows = useMemo(() => customers.map(c => bookRow(c, today)), [customers, today]);
    const lastNotes = useLastNotes(context.workspace.id, useMemo(() => customers.map(c => c.id), [customers]));
    const { money } = useFormatters();
    // Why each account is here: stated reasons from its own facts, never a score (Phase 0).
    const reasonsOf = usePriorityReasons(context, rows);

    // The list's own question, carried into each account so "Next" walks this
    // queue and "Back" returns to it.
    const query = new URLSearchParams(worklistQuery(group));
    if (mine) query.set('owner', 'mine');
    query.set('sort', sort);
    const inCustomers = `${to.customers}?${query}`;
    query.set('from', 'today');
    query.set('group', group);

    return (
        <div>
            <div className="px-5 py-3 flex items-center gap-x-4 gap-y-2 flex-wrap text-secondary text-label-3">
                {scope}
                <span className="flex-1 min-w-48">
                    {figure && figure.count > 0 && (
                        <><strong className="font-semibold text-label num" title={money.exact(figure.amount)}>{money.compact(figure.amount)}</strong>{' '}
                            across {count(figure.count)} {figure.count === 1 ? 'account' : 'accounts'} · </>
                    )}
                    {definition.hint}. {ORDER_NOTE[group]}
                </span>
                {figure && figure.count > 0 && (
                    <Link to={inCustomers} className="text-accent-text font-semibold hover:underline">
                        Open in Customers
                    </Link>
                )}
            </div>
            <div aria-busy={loading || undefined} className={cx('transition-opacity duration-150', loading && rows.length > 0 && 'opacity-60')}>
                {loading && rows.length === 0 ? (
                    <LoadingList label="Loading the accounts" rows={6} />
                ) : error ? (
                    <div className="px-5 py-6 space-y-3">
                        <FormNotice tone="error">{error}</FormNotice>
                        <Button variant="secondary" onClick={() => { void reload(); }}>Try again</Button>
                    </div>
                ) : rows.length === 0 ? (
                    <EmptyState compact title={definition.empty} hint="Pick another group above." />
                ) : (
                    <AccountTable
                        rows={rows}
                        lastNotes={lastNotes}
                        label={`${definition.label} accounts`}
                        showAssignee={holds(context, 'customers.view.all') && !mine}
                        linkTo={id => `${to.customer(id)}?${query}`}
                        assigneeOf={row => assigneeName(row, names)}
                        {...(reasonsOf ? { reasonsOf } : {})}
                    />
                )}
            </div>
            {hasMore && !loading && (
                <div className="px-5 py-3 border-t border-separator text-center">
                    <Button variant="quiet" size="sm" onClick={() => { void loadMore(); }} busy={loadingMore}>
                        {loadingMore ? 'Loading…' : `Show more (${count(loadedCount)} of ${count(figure?.count ?? loadedCount)})`}
                    </Button>
                </div>
            )}
        </div>
    );
}

/* -------------------------------------------------------------------------- */

/**
 * The queue's groups as one control: a row of tabs whose underline is one mark that slides to the open group
 * (`useIndicator`), rather than a border that blinks off under one tab and on under the next.
 */
function QueueGroups({ active, children }: { active: string; children: React.ReactNode }) {
    const { track, style, shown, animate } = useIndicator<HTMLDivElement>(active);
    return (
        <div ref={track} role="group" aria-label="Which accounts" className="relative flex items-end gap-1 overflow-x-auto">
            {shown && (
                <span aria-hidden="true" data-indicator="" style={style}
                    className={cx('absolute left-0 bottom-0 h-0.5 rounded-full bg-accent', animate && 'transition-[transform,width] duration-200 ease-out-quart')} />
            )}
            {children}
        </div>
    );
}

/** A part of the side column beside the queue: secondary, compact, and never louder than the work. */
function Side({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
    return (
        <section className="px-4 pt-3.5 pb-3">
            <div className="flex items-center justify-between gap-3 min-h-7">
                <h3 className="font-display stretch-title text-body font-semibold text-label">{title}</h3>
                {action && <span className="flex-none whitespace-nowrap">{action}</span>}
            </div>
            <div className="mt-1.5">{children}</div>
        </section>
    );
}

/** One row of figures in the side column: no rule between rows; the hover says it opens. */
const sideRow = '-mx-2 px-2 h-9 rounded-sm flex items-center justify-between gap-3 hover:bg-hover focus-visible:bg-hover outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset';

/**
 * What customers said they would pay. A promise is not money, and the wording
 * never calls it money that has arrived. Each figure opens the promises it
 * counted, in Customers (0032).
 */
function Promised({ totals }: { totals: { promisedToday: WorklistFigure; promisedWeek: WorklistFigure; promisedOpen: WorklistFigure } }) {
    const context = useActiveWorkspace();
    const { money, count } = useFormatters();
    const to = paths(context.workspace.slug);
    const rows: [string, WorklistFigure, string][] = [
        ['Due today', totals.promisedToday, 'promise_today'],
        ['Next 7 days', totals.promisedWeek, 'promise_week'],
        ['All open promises', totals.promisedOpen, 'promise_open'],
    ];
    return (
        <Side title="Promised">
            <ul>
                {rows.map(([label, f, filter]) => (
                    <li key={label}>
                        <Link
                            to={`${to.customers}?followUp=${filter}`}
                            aria-label={`Promised, ${label.toLowerCase()}: ${count(f.count)}, ${money.exact(f.amount)}`}
                            className={sideRow}
                        >
                            <span className="text-secondary text-label-2">{label} <span className="text-label-3 num">· {count(f.count)}</span></span>
                            <span className="text-secondary font-semibold text-label num">{money.compact(f.amount)}</span>
                        </Link>
                    </li>
                ))}
                <li>
                    <Link to={`${to.customers}?followUp=promise_passed`} className={sideRow}>
                        <span className="text-secondary text-label-2">Promise date passed</span>
                        <span className="text-secondary font-semibold text-accent-text">Open</span>
                    </Link>
                </li>
            </ul>
            <p className="pt-1.5 text-caption text-label-3">Committed by customers, not yet received.</p>
        </Side>
    );
}

const TONE_TEXT = { dang: 'text-dang', warn: 'text-warn', brand: 'text-accent-text', pos: 'text-pos', neutral: 'text-label' } as const;

/**
 * The cheques that need something done to them. Four of the six states,
 * because "upcoming" and "cleared" are not work. Both numbers come from
 * `cheque_totals()`, the call the register's own figures make.
 */
function ChequeAttention({ cheques, unavailable }: {
    cheques: { byState: Record<ChequeState, { count: number; amount: number }> };
    unavailable: boolean;
}) {
    const context = useActiveWorkspace();
    const { money, count } = useFormatters();
    const to = paths(context.workspace.slug);
    const needs: ChequeState[] = ['overdue', 'due', 'bounced', 'hold'];

    if (unavailable) {
        return <Side title="Cheques"><p className="text-secondary text-label-3">We could not read your cheque figures just now.</p></Side>;
    }
    const waiting = needs.filter(state => cheques.byState[state].count > 0);
    if (waiting.length === 0) return null;

    return (
        <Side title="Cheques needing attention" action={<Link to={to.cheques} className="text-secondary text-accent-text font-semibold hover:underline">All cheques</Link>}>
            <ul>
                {waiting.map(state => (
                    <li key={state}>
                        <Link
                            to={`${to.cheques}?state=${state}`}
                            aria-label={`${CHEQUE_STATES[state].label}: ${count(cheques.byState[state].count)} cheques, ${money.exact(cheques.byState[state].amount)}`}
                            className={sideRow}
                        >
                            <span className="text-secondary text-label-2">
                                {CHEQUE_STATES[state].label} <span className={cx('num font-semibold', TONE_TEXT[CHEQUE_STATES[state].tone])}>· {count(cheques.byState[state].count)}</span>
                            </span>
                            <span className="text-secondary font-semibold text-label num">{money.compact(cheques.byState[state].amount)}</span>
                        </Link>
                    </li>
                ))}
            </ul>
        </Side>
    );
}

/**
 * Who on the team has work waiting — "is anybody drowning", sorted by late
 * follow-ups because that is the one that means somebody needs help today.
 * Anything deeper belongs in Reports.
 */
function TeamWork({ team }: { team: MemberWorkload[] }) {
    const context = useActiveWorkspace();
    const { money, count } = useFormatters();
    const to = paths(context.workspace.slug);
    return (
        <Side title="Team" action={<Link to={to.reports} className="text-secondary text-accent-text font-semibold hover:underline">Reports</Link>}>
            {/* Fixed columns, so a long name gives way (truncated, whole in its title) rather than pushing the figures out of the side column. */}
            <div className="-mx-4">
            <Table caption="Follow-ups waiting per person" className="table-fixed">
                <thead>
                    <tr>
                        <Th className="!pl-4 !bg-transparent">Person</Th>
                        <Th numeric className="!px-2 w-16 !bg-transparent">Late</Th>
                        <Th numeric className="!px-2 w-13 !bg-transparent">Today</Th>
                        <Th numeric className="!pr-4 !pl-2 w-19 !bg-transparent">Owed</Th>
                    </tr>
                </thead>
                <tbody>
                    {team.map(member => {
                        const cover = followUpCoverage({ total: member.openCount, overdue: member.overdueCount, noFollowUp: member.noFollowUpCount, writtenOff: member.badDebtCount });
                        return (
                        <tr key={member.memberId}>
                            <RowHead className="!pl-4 !h-12 text-secondary whitespace-nowrap">
                                <Link to={`${to.customers}?owner=${member.memberId}`} title={member.displayName} className="hover:text-accent-text hover:underline block truncate">
                                    {member.displayName}
                                </Link>
                                {cover.share !== null && (
                                    <ProportionBar
                                        share={cover.share}
                                        tone={cover.share >= 0.8 ? 'pos' : cover.share >= 0.5 ? 'warn' : 'dang'}
                                        label={`${member.displayName}: ${cover.planned} of ${cover.total} accounts have a plan`}
                                        className="!h-1 mt-1 max-w-24"
                                    />
                                )}
                            </RowHead>
                            <Td numeric className={cx('!h-10 !px-2 text-secondary', member.overdueCount > 0 ? 'text-warn font-semibold' : 'text-label-3')}>{count(member.overdueCount)}</Td>
                            <Td numeric className="!h-10 !px-2 text-secondary text-label-2">{count(member.todayCount)}</Td>
                            <Td numeric className="!h-10 !pr-4 !pl-2 text-secondary text-label">{money.compact(member.openAmount)}</Td>
                        </tr>
                        );
                    })}
                </tbody>
            </Table>
            </div>
            <p className="pt-2 text-caption text-label-3">The bar under a name is how much of their book has a plan.</p>
        </Side>
    );
}

/* -------------------------------------------------------------------------- */

/**
 * How much of the book has a plan (PRODUCT-UX-FOUNDATION §7): the accounts
 * that owe and have a next follow-up still to come, or are marked collected,
 * over all the accounts that owe. Two counts from `worklist_summary()` and
 * their proportion — never a score. The rest are exactly the late and
 * undated accounts, and the link opens them.
 */
function CoveragePanel({ totals }: { totals: WorklistTotals }) {
    const context = useActiveWorkspace();
    const { count } = useFormatters();
    const to = paths(context.workspace.slug);
    const cover = followUpCoverage({
        total: totals.total.count, overdue: totals.groups.overdue.count, noFollowUp: totals.groups.no_follow_up.count,
        writtenOff: totals.groups.bad_debt.count,
    });
    if (cover.share === null) return null;
    const percent = Math.round(cover.share * 100);
    const without = cover.total - cover.planned;
    return (
        <Side title="Follow-up coverage">
            <div className="pb-1">
                <div className="flex items-baseline justify-between gap-3">
                    <span className="text-title font-semibold text-label num leading-tight">{percent}%</span>
                    <span className="text-caption text-label-3 num">{count(cover.planned)} of {count(cover.total)} accounts</span>
                </div>
                <ProportionBar
                    share={cover.share}
                    tone={cover.share >= 0.8 ? 'pos' : cover.share >= 0.5 ? 'warn' : 'dang'}
                    label={`${percent}% — ${cover.planned} of ${cover.total} accounts that owe have a plan`}
                    className="mt-2"
                />
                <p className="text-caption text-label-3 mt-2">Owing accounts with a next follow-up still to come, or marked collected. Accounts marked as bad debt are left out.</p>
            </div>
            {without > 0 && (
                <Link to={`${to.customers}?${UNPLANNED_QUERY}`} className={sideRow}>
                    <span className="text-secondary text-label-2">Late or no date <span className="text-label-3 num">· {count(without)}</span></span>
                    <span className="text-secondary font-semibold text-accent-text">Open</span>
                </Link>
            )}
        </Side>
    );
}

/**
 * How old the money on this person's book is, as one bar and a door per band —
 * the report's own ageing (`report_ageing()`), so the two are one figure: a
 * sheet's four bands, or a TallyPrime company's own windows, named as
 * Settings names them (D3), with what is not yet due beside them.
 */
function AgeingPanel({ bands: rows }: { bands: TodayAgeingBand[] }) {
    const context = useActiveWorkspace();
    const { money, count } = useFormatters();
    const to = paths(context.workspace.slug);
    const aged = agedRows(rows);
    const bands = [...aged, ...rows.filter(r => r.kind === 'not_yet_due' && r.amount > 0)];
    const total = aged.reduce((sum, b) => sum + b.amount, 0);
    if (total <= 0) return null;
    // A TallyPrime company's windows hold open bills only: credits and money in no bucket are beside them, never in
    // them, and with them make the net (docs/tally/18 §4). A sheet's bands are its own; nothing is added to them.
    const amountOf = (kind: string) => rows.filter(r => r.kind === kind).reduce((sum, r) => sum + r.amount, 0);
    const credits = amountOf('credits'), notAged = amountOf('not_aged'), notYetDue = amountOf('not_yet_due');
    const windowsNet = aged.some(r => r.kind === 'window') && (credits > 0 || notAged > 0)
        ? Math.round((total + notYetDue + notAged - credits) * 100) / 100 : null;
    const label = (bucket: string) => ageingRowLabel(rows.find(r => r.bucket === bucket)!, rows);
    const swatch = (i: number) => (i < aged.length ? ageSwatch(i, aged.length) : 'bg-card-3');
    return (
        <Side title="How old the money is" action={<Link to={to.reports} className="text-secondary text-accent-text font-semibold hover:underline">Reports</Link>}>
            <div className="pt-1 pb-2">
                <AgeingBar
                    parts={aged.map(b => b.amount)}
                    label={aged.map(b => `${label(b.bucket)} ${Math.round((b.amount / total) * 100)}%`).join(', ')}
                />
            </div>
            <ul>
                {bands.map((band, i) => (
                    <li key={band.bucket}>
                        <Link
                            to={`${to.customers}?ageing=${band.filterValue}&settlement=all`}
                            aria-label={`${label(band.bucket)}: ${money.exact(band.amount)} across ${count(band.accountCount)} accounts`}
                            className={sideRow}
                        >
                            <span className="text-secondary text-label-2 flex items-center gap-2 min-w-0">
                                <span aria-hidden="true" className={cx('h-2.5 w-2.5 rounded-full flex-none', swatch(i))} />
                                <span className="truncate">{label(band.bucket)}</span>
                                <span className="text-label-3 num">· {Math.round((band.amount / total) * 100)}%</span>
                            </span>
                            <span className="text-secondary font-semibold text-label num">{money.compact(band.amount)}</span>
                        </Link>
                    </li>
                ))}
            </ul>
            {windowsNet !== null && (
                <p className="pt-1.5 text-caption text-label-3" data-testid="ageing-net">
                    {notAged > 0 && <>Not in any bucket {money.compact(notAged)} · </>}
                    {credits > 0 && <>Credits, against no bill yet − {money.compact(credits)} · </>}
                    <span className="text-label-2">Net receivable <span className="num font-semibold">{money.balance(Math.abs(windowsNet), windowsNet < 0 ? 'Cr' : 'Dr')}</span></span>
                </p>
            )}
        </Side>
    );
}
